// 📄 services/solucionIntegrationService.ts
// Persistencia genérica de los Canales de Integración de SLP/Sourcing/
// Contracts — mismo patrón que commerce/integrationService.ts
// (saveGeneralsConfig/savePurchaseOrderConfig), pero parametrizado por
// solución y canal en vez de un archivo por canal. Vive en el mismo doc
// `projects/{id}`, en el campo `{solucion}Integration` que ya estaba
// declarado (sin usar) en la interfaz Project (ver projectService.ts).
//
// A diferencia de Commerce (donde `checklistProgress` es plano por
// step id, porque cada canal vive en su propio archivo/ruta y los ids de
// paso no chocan entre canales), acá el progreso se namespacea por canal
// (`checklistProgress.{canalSlug}.{stepId}`) porque una misma solución
// puede tener varios canales con ids de paso repetidos (todos usan los
// mismos ids genéricos: parametros/mapeo/prueba, ver integrationChannels.ts).
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { SolucionConCanales } from "../pages/Proyectos/integrationChannels.ts";
import { CustomDevelopmentData, emptyCustomDevelopment } from "../types/customDevelopment.ts";
import { TransportConfirmation } from "../types/transportConfirmation.ts";

export type Ambiente = "DEV" | "QAS" | "PRD";

const CAMPO_POR_SOLUCION: Record<SolucionConCanales, string> = {
  slp: "slpIntegration",
  sourcing: "sourcingIntegration",
  contracts: "contractsIntegration"
};

export interface CanalFormData {
  [campo: string]: any;
  requiresOT?: boolean;
  otNumber?: string;
  comments?: string;
  customDevelopment?: CustomDevelopmentData;
}

/** Trae el objeto completo `{solucion}Integration` del proyecto (todas sus ambientes/canales), o null. */
export const getCanalIntegracionConfig = async (
  projectId: string,
  solucion: SolucionConCanales
): Promise<any | null> => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");
  const snap = await getDoc(doc(db, "projects", projectId));
  if (!snap.exists()) return null;
  return snap.data()[CAMPO_POR_SOLUCION[solucion]] || null;
};

/** Guarda los campos de UN paso de UN canal, en UN ambiente, y marca el progreso del checklist. */
export const saveCanalIntegracionConfig = async (
  projectId: string,
  solucion: SolucionConCanales,
  canalSlug: string,
  environment: Ambiente,
  activeStep: string,
  formData: CanalFormData
): Promise<void> => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");

  const campo = CAMPO_POR_SOLUCION[solucion];
  const envPath = `${campo}.environments.${environment}`;
  const { requiresOT, otNumber, comments, customDevelopment, ...camposDelPaso } = formData;

  const updates: Record<string, any> = {
    [`${envPath}.channels.${canalSlug}.step_${activeStep}`]: {
      ...camposDelPaso,
      requiresOT: !!requiresOT,
      otNumber: requiresOT ? otNumber || "" : "",
      comments: comments || "",
      customDevelopment: customDevelopment || emptyCustomDevelopment()
    },
    [`${envPath}.checklistProgress.${canalSlug}.${activeStep}`]: true
  };

  await updateDoc(doc(db, "projects", projectId), updates);
};

/** Igual que saveTransportConfirmation en commerce/integrationService.ts, pero para canales genéricos (SLP/Sourcing/Contracts). */
export const saveTransportConfirmationGenerico = async (
  projectId: string,
  solucion: SolucionConCanales,
  canalSlug: string,
  activeStep: string,
  environment: 'QAS' | 'PRD',
  data: TransportConfirmation
): Promise<void> => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");

  const campo = CAMPO_POR_SOLUCION[solucion];
  const path = `${campo}.environments.DEV.channels.${canalSlug}.step_${activeStep}.transport.${environment}`;

  await updateDoc(doc(db, "projects", projectId), { [path]: data });
};

/** Igual que saveManualConfigDate en commerce/integrationService.ts, pero para canales genéricos (SLP/Sourcing/Contracts). */
export const saveManualConfigDateGenerico = async (
  projectId: string,
  solucion: SolucionConCanales,
  canalSlug: string,
  activeStep: string,
  environment: 'QAS' | 'PRD',
  date: string
): Promise<void> => {
  if (!projectId) throw new Error("ID de proyecto no proporcionado.");

  const campo = CAMPO_POR_SOLUCION[solucion];
  const path = `${campo}.environments.${environment}.channels.${canalSlug}.step_${activeStep}.configuredAt`;

  await updateDoc(doc(db, "projects", projectId), { [path]: date });
};
